import { bridge } from '~/services/bridge'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { UploadOptions } from '~/utils/presignedUpload'

export type UploadFileOptions = UploadOptions

export interface LocalSyncRepo {
  filename: string
  path: string
  uploaded_at: string
}

export type SecurityScanStatus = 'CLEAN' | 'SCANNING' | 'THREATS_FOUND'

/**
 * Upload a repository file for local synchronization
 * @param workspaceUuid - Workspace UUID for workspace-aware upload
 * @param file - File to upload
 * @param options - Upload options including callbacks
 */
export async function uploadLocalSyncRepo(
  workspaceUuid: string,
  file: File,
  options: UploadFileOptions
) {
  const {
    onProgress = () => {},
    onComplete = (_path: string) => {},
    onError = () => {},
    onVirusScanStatus = () => {}
  } = options

  const formData = new FormData()
  formData.append('file', file)
  const deviceId = window.getDeviceId()

  try {
    const { resolveApiUrl } = await import('~/utils/apiUrl')
    const headers: Record<string, string> = {}
    if (bridge.isInPlugin()) {
      headers['X-Curietech-Plugin'] = 'true'
    }
    const response = await fetch(
      resolveApiUrl(`/api/workspace/${workspaceUuid}/local-sync/${deviceId}`),
      {
        method: 'PUT',
        credentials: 'include',
        headers,
        body: formData
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      console.error(`🔄 Error response body: ${errorText}`)
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      console.error(`🔄 Response body is not readable`)
      throw new Error('Response body is not readable')
    }

    // Track if we've already handled a completion or error
    let completed = false

    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        break
      }

      const chunk = new TextDecoder().decode(value)
      const lines = chunk.split('\n').filter(line => line.trim() !== '')

      for (const line of lines) {
        try {
          // Skip if we've already handled completion or error
          if (completed) continue

          const data = JSON.parse(line)
          console.debug(`🔄 Received JSON response:`, data)

          // Process error messages
          if (data.error) {
            console.error(`🔄 Server reported error: ${data.error}`)

            // Extract security status if available
            if (data.virus_scan_status) {
              // Notify about the virus scan status
              onVirusScanStatus(data.virus_scan_status, data.message || data.error)
              onError(data.error, data.virus_scan_status as SecurityScanStatus)
            } else {
              onError(data.error)
            }
            completed = true
            return
          }

          // Process upload progress
          if (data.progress) {
            onProgress(data.progress)
          }

          // Process scanning status updates
          if (data.status === 'scanning') {
            // Make sure progress is shown as complete when scanning
            onProgress(100)

            // Report virus scanning in progress
            onVirusScanStatus('SCANNING', data.message || 'Virus scan in progress')

            // Include scan progress if available
            if (data.scan_progress) {
              onVirusScanStatus(
                'SCANNING',
                `Virus scan in progress: ${Math.round(data.scan_progress)}%` +
                  (data.message ? ` (${data.message})` : '')
              )
            }
          }

          // Process scan completion
          if (data.status === 'scan_complete') {
            onVirusScanStatus(
              data.virus_scan_status as SecurityScanStatus,
              data.message || `Virus scan completed: ${data.virus_scan_status}`
            )

            // If scan completed with threats found, report error but don't throw
            if (data.virus_scan_status === 'THREATS_FOUND') {
              const errorMsg = data.message || 'Security threats detected in uploaded file'
              console.error(`🔄 Error uploading ${file.name}: ${errorMsg}`)
              onError(errorMsg, data.virus_scan_status)
              completed = true
              return
            }

            // If scan completed successfully, mark as complete
            if (
              data.virus_scan_status === 'NO_THREATS_FOUND' ||
              data.virus_scan_status === 'SCAN_SKIPPED' ||
              data.virus_scan_status === 'CLEAN'
            ) {
              onComplete(data.path || '', data.virus_scan_status)
              completed = true
              return
            }
          }

          // Process final completion
          if (data.status === 'completed') {
            // Handle virus scan status if available
            if (data.virus_scan_status) {
              onVirusScanStatus(
                data.virus_scan_status as SecurityScanStatus,
                data.message || `Upload completed with status: ${data.virus_scan_status}`
              )

              // If threats found, report error but don't throw an exception
              if (data.virus_scan_status === 'THREATS_FOUND') {
                const errorMsg = data.message || 'Security threats detected in uploaded file'
                console.error(`🔄 Error uploading ${file.name}: ${errorMsg}`)
                onError(errorMsg, data.virus_scan_status)
                completed = true
                return
              }

              onComplete(data.path, data.virus_scan_status as SecurityScanStatus)
            } else {
              // If no virus scan status, assume CLEAN
              onComplete(data.path, 'CLEAN')
            }
            completed = true
            return
          }
        } catch (parseError) {
          console.error(`🔄 Failed to parse JSON line: ${line}`, parseError)
          // Continue processing other lines without throwing
        }
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    console.error(`🔄 Upload failed with error:`, error)
    onError(errorMessage)
  }
}

/**
 * List local sync repositories
 * @param workspaceUuid - Workspace UUID for workspace-aware listing
 */
export async function listLocalSyncRepos(
  workspaceUuid: string
): Promise<{ repositories: LocalSyncRepo[] }> {
  const deviceId = window.getDeviceId()
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/local-sync/${deviceId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    const error = await response.text()
    console.error(`🔄 Failed to list repositories: ${error}`)
    throw new Error(error)
  }

  const result = await response.json()
  return result
}

/**
 * Delete a specific local sync repository
 * @param workspaceUuid - Workspace UUID for workspace-aware deletion
 * @param repoName - Repository name to delete
 */
export async function deleteLocalSyncRepo(workspaceUuid: string, repoName: string) {
  const deviceId = window.getDeviceId()
  const params = new URLSearchParams({ filename: repoName }).toString()
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/local-sync/${deviceId}?${params}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }

  return response.json()
}

/**
 * Keep only the specified local sync repositories
 * @param workspaceUuid - Workspace UUID for workspace-aware deletion
 * @param repoNames - Repository names to keep
 */
export async function keepOnlyTheseLocalSyncRepos(workspaceUuid: string, repoNames: string[]) {
  const deviceId = window.getDeviceId()
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/local-sync/${deviceId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(repoNames)
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }

  return response.json()
}

/**
 * Delete all local sync repositories
 * @param workspaceUuid - Workspace UUID for workspace-aware deletion
 */
export async function deleteAllLocalSyncRepos(workspaceUuid: string) {
  const deviceId = window.getDeviceId()

  try {
    const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/local-sync/${deviceId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify([])
    })

    if (!response.ok) {
      const error = await response.text()

      // Handle specific error cases gracefully
      if (response.status === 404) {
        console.info(`🔄 Device ${deviceId} not found on server (this is normal for new devices)`)
        return { success: true, message: 'Device not found - nothing to delete' }
      }

      if (response.status >= 500) {
        console.warn(`🔄 Server error during repository cleanup (${response.status}): ${error}`)
        // Don't throw for server errors during cleanup - this shouldn't break initialization
        return { success: false, message: `Server error: ${error}` }
      }

      throw new Error(error)
    }

    const result = await response.json()
    console.info(`🔄 Successfully cleaned up device repositories for ${deviceId}`)
    return result
  } catch (error) {
    // Handle network errors gracefully
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.warn(
        '🔄 Network error during repository cleanup - this is non-critical during initialization'
      )
      return { success: false, message: 'Network error during cleanup' }
    }

    // Re-throw other errors
    throw error
  }
}

/**
 * Synchronize local sync repositories with backend
 * @param workspaceUuid - Workspace UUID for workspace-aware sync
 * @param deviceId - Device identifier
 * @param repoNames - List of repository names to sync
 */
export const syncLocalSyncRepos = async (
  workspaceUuid: string,
  deviceId: string,
  repoNames: string[]
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await fetchWrapper(
      `/api/workspace/${workspaceUuid}/local-sync/${deviceId}/sync`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(repoNames)
      }
    )

    return await response.json()
  } catch (error) {
    // Silently handle network errors (e.g., server unreachable)
    // These are expected during startup or when offline
    const isNetworkError = error instanceof TypeError && error.message === 'Failed to fetch'
    if (!isNetworkError) {
      console.error('Error syncing local repositories:', error)
    }
    throw error
  }
}
